import type { Plugin } from 'chart.js'

type TemporalMarkerPluginOptions = {
  markerIndex: number
  // Line
  lineColor?: string
  lineDash?: number[]
  lineWidth?: number
  // Icon dimensions
  iconRadius?: number
  innerRadius?: number
  // Icon colors
  bgColor?: string
  bgColorHover?: string
  borderColor?: string
  iconColor?: string
  iconColorHover?: string
  // Tooltip
  tooltipTitle?: string
  tooltipBody?: string
  tooltipMaxWidth?: number
}

// Defaults derived from the reference SVG + CSS
const DEFAULTS = {
  lineColor: '#d99a2b',
  lineDash: [6, 4] as number[],
  lineWidth: 1,
  iconRadius: 10,
  innerRadius: 8,
  bgColor: '#fdf3df',
  bgColorHover: '#fbe9c4',
  borderColor: '#e6c178',
  iconColor: '#b8860b',
  iconColorHover: '#8a5a12',
  tooltipMaxWidth: 240
}

const TOOLTIP_PADDING = 12
const BODY_LINE_HEIGHT = 17

type DrawTooltipOptions = {
  anchorX: number
  anchorY: number
  iconRadius: number
  title: string
  body: string
  maxWidth: number
  chartWidth: number
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (current && ctx.measureText(test).width > maxWidth) {
      lines.push(current)
      current = word
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  return lines
}

function drawTooltip(
  ctx: CanvasRenderingContext2D,
  {
    anchorX,
    anchorY,
    iconRadius,
    title,
    body,
    maxWidth,
    chartWidth
  }: DrawTooltipOptions
) {
  ctx.save()

  ctx.font = '12px sans-serif'
  const bodyLines = body ? wrapText(ctx, body, maxWidth) : []
  const bodyBlockHeight =
    bodyLines.length > 0 ? 8 + bodyLines.length * BODY_LINE_HEIGHT : 0

  const tooltipWidth = maxWidth + TOOLTIP_PADDING * 2
  const tooltipHeight = TOOLTIP_PADDING * 2 + 13 + bodyBlockHeight

  let tx = anchorX - tooltipWidth / 2
  if (tx < 4) tx = 4
  if (tx + tooltipWidth > chartWidth - 4) tx = chartWidth - tooltipWidth - 4
  const ty = anchorY + iconRadius + 6

  ctx.shadowColor = 'rgba(0,0,0,0.12)'
  ctx.shadowBlur = 12
  ctx.shadowOffsetY = 4
  ctx.fillStyle = '#ffffff'
  roundRect(ctx, tx, ty, tooltipWidth, tooltipHeight, 8)
  ctx.fill()

  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.strokeStyle = 'rgba(0,0,0,0.08)'
  ctx.lineWidth = 1
  roundRect(ctx, tx, ty, tooltipWidth, tooltipHeight, 8)
  ctx.stroke()

  ctx.fillStyle = '#1a1a1a'
  ctx.font = 'bold 13px sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillText(title, tx + TOOLTIP_PADDING, ty + TOOLTIP_PADDING)

  if (bodyLines.length > 0) {
    ctx.fillStyle = '#555555'
    ctx.font = '12px sans-serif'
    bodyLines.forEach((line, i) => {
      ctx.fillText(
        line,
        tx + TOOLTIP_PADDING,
        ty + TOOLTIP_PADDING + 13 + 8 + i * BODY_LINE_HEIGHT
      )
    })
  }

  ctx.restore()
}

function drawIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  hovered: boolean,
  opts: Required<
    Pick<
      TemporalMarkerPluginOptions,
      | 'iconRadius'
      | 'innerRadius'
      | 'bgColor'
      | 'bgColorHover'
      | 'borderColor'
      | 'iconColor'
      | 'iconColorHover'
    >
  >
) {
  const {
    iconRadius,
    innerRadius,
    bgColor,
    bgColorHover,
    borderColor,
    iconColor,
    iconColorHover
  } = opts
  const fg = hovered ? iconColorHover : iconColor
  const scale = iconRadius / 7 // SVG viewBox center = 7

  ctx.save()

  // Outer bg circle
  ctx.beginPath()
  ctx.arc(cx, cy, iconRadius, 0, Math.PI * 2)
  ctx.fillStyle = hovered ? bgColorHover : bgColor
  ctx.fill()
  ctx.strokeStyle = borderColor
  ctx.lineWidth = 1
  ctx.stroke()

  // Inner circle — SVG <circle r="5.5" stroke-width="1">
  ctx.beginPath()
  ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2)
  ctx.strokeStyle = fg
  ctx.lineWidth = 1
  ctx.stroke()

  // "i" stem — SVG M7 6.2V9.5: from (0,-0.8) to (0,2.5) relative to center
  ctx.beginPath()
  ctx.moveTo(cx, cy + -0.8 * scale)
  ctx.lineTo(cx, cy + 2.5 * scale)
  ctx.strokeStyle = fg
  ctx.lineWidth = 1.2
  ctx.lineCap = 'round'
  ctx.stroke()

  // "i" dot — SVG M7 4.5v.01: at (0,-2.5) from center
  ctx.beginPath()
  ctx.arc(cx, cy + -2.5 * scale, 0.7, 0, Math.PI * 2)
  ctx.fillStyle = fg
  ctx.fill()

  ctx.restore()
}

export function createTemporalMarkerPlugin(
  opts: TemporalMarkerPluginOptions
): Plugin<'line'> {
  const {
    markerIndex,
    lineColor = DEFAULTS.lineColor,
    lineDash = DEFAULTS.lineDash,
    lineWidth = DEFAULTS.lineWidth,
    iconRadius = DEFAULTS.iconRadius,
    innerRadius = DEFAULTS.innerRadius,
    bgColor = DEFAULTS.bgColor,
    bgColorHover = DEFAULTS.bgColorHover,
    borderColor = DEFAULTS.borderColor,
    iconColor = DEFAULTS.iconColor,
    iconColorHover = DEFAULTS.iconColorHover,
    tooltipTitle,
    tooltipBody,
    tooltipMaxWidth = DEFAULTS.tooltipMaxWidth
  } = opts

  let iconX = 0
  let iconY = 0
  let isHovered = false

  return {
    id: 'temporalMarkerPlugin',

    beforeDraw(chart) {
      if (!isHovered || markerIndex < 0) return
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
    },

    afterDraw(chart) {
      if (markerIndex < 0) return

      const ctx = chart.ctx
      const xScale = chart.scales.x
      const { top, bottom } = chart.chartArea

      iconX = xScale.getPixelForValue(markerIndex - 0.5)
      iconY = top

      // Dashed line from bottom of icon to chart bottom
      ctx.save()
      ctx.beginPath()
      ctx.setLineDash(lineDash)
      ctx.strokeStyle = lineColor
      ctx.lineWidth = lineWidth
      ctx.moveTo(iconX, iconY + iconRadius)
      ctx.lineTo(iconX, bottom)
      ctx.stroke()
      ctx.restore()

      drawIcon(ctx, iconX, iconY, isHovered, {
        iconRadius,
        innerRadius,
        bgColor,
        bgColorHover,
        borderColor,
        iconColor,
        iconColorHover
      })

      if (isHovered && tooltipTitle) {
        drawTooltip(ctx, {
          anchorX: iconX,
          anchorY: iconY,
          iconRadius,
          title: tooltipTitle,
          body: tooltipBody ?? '',
          maxWidth: tooltipMaxWidth,
          chartWidth: chart.width
        })
      }
    },

    afterEvent(chart, { event }) {
      if (markerIndex < 0) return

      const ex = (event as unknown as { x: number }).x ?? -1
      const ey = (event as unknown as { y: number }).y ?? -1
      const dist = Math.hypot(ex - iconX, ey - iconY)
      const hovered = dist <= iconRadius

      if (hovered !== isHovered) {
        isHovered = hovered
        chart.canvas.style.cursor = hovered ? 'help' : ''
        chart.draw()
      }
    }
  }
}
